import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquare,
  Target,
  Send,
  Inbox,
  ArrowLeft,
  Sparkles,
} from 'lucide-react';
import { useDmdilly } from './dmdilly-context';
import CreatorInterview from './creator-interview';
import CreatorFocus from './creator-focus';
import PriorityInbox from './priority-inbox';

const NAV_ITEMS = [
  { id: 'focus', label: 'Focus', icon: Target },
  { id: 'inbox', label: 'Inbox', icon: Inbox },
  { id: 'interview', label: 'Update Focus', icon: MessageSquare },
  { id: 'preview', label: 'Preview Fan Page', icon: Send },
];

export default function DmdillyPage() {
  const { creatorId } = useDmdilly();
  const { guideInfo } = useDmdilly();
  const [view, setView] = useState('focus');

  const renderContent = () => {
    switch (view) {
      case 'interview':
        return (
          <div className="bg-white rounded-2xl shadow-sm h-[600px] overflow-hidden">
            <CreatorInterview onComplete={() => setView('focus')} />
          </div>
        );

      case 'focus':
        return (
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <CreatorFocus />
          </div>
        );

      case 'inbox':
        return (
          <div className="bg-white rounded-2xl shadow-sm h-[700px] overflow-hidden">
            <PriorityInbox />
          </div>
        );

      default:
        return <HomePage onNavigate={setView} creatorId={creatorId} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">

        <AnimatePresence mode="wait">
          <motion.div
            key={view}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            {view !== 'home' && (
              <button
                onClick={() => setView('home')}
                className="flex items-center gap-2 text-gray-500 hover:text-boggler-teal mb-6 transition-colors"
              >
                <ArrowLeft size={16} />
                Back to Home
              </button>
            )}
            <div style={{ display: "flex", flexDirection: "row", alignItems: "flex-end", gap: "8px", marginBottom: "16px", width: "100%" }}>
              {NAV_ITEMS.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => item.id === "preview" ? window.open('https://theboggler.com/@'+guideInfo?.username, '_blank') : setView(item.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      view === item.id
                        ? 'bg-boggler-teal/10 text-boggler-teal'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon size={16} />
                    <span className="hidden sm:inline">{item.label}</span>
                  </button>
                );
              })}
            </div>
            {renderContent()}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-6 mt-12">
        <div className="max-w-6xl mx-auto px-6 text-center text-sm text-gray-500">
          <p>dmdilly • Creator-focused DM prioritization</p>
        </div>
      </footer>
    </div>
  );
}

// Home Page Component
function HomePage({ onNavigate, creatorId }) {
  const cards = [
    {
      id: 'focus',
      title: 'Current Focus',
      description: 'View and manage your saved intent profile.',
      icon: Target,
      color: 'from-blue-500 to-blue-700',
    },
    {
      id: 'inbox',
      title: 'Priority Inbox',
      description: 'Review and manage scored submissions.',
      icon: Inbox,
      color: 'from-orange-500 to-orange-700',
    },
  ];

  return (
    <div className="space-y-8">
      {/* Hero */}
      <div className="text-center py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-boggler-teal/10 text-boggler-teal rounded-full text-sm font-medium mb-6"
        >
          <Sparkles size={16} />
          AI-Powered DM Prioritization
        </motion.div>
        <h1 className="text-4xl font-bold text-gray-800 mb-4">
          Welcome to dmdilly
        </h1>
        <p className="text-gray-500 max-w-lg mx-auto">
          Define your current focus, let clients answer adaptive questions, and
          receive prioritized messages scored by AI.
        </p>
      </div>

      {/* Cards Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {cards.map((card, index) => {
          const Icon = card.icon;
          return (
            <motion.div
              key={card.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => onNavigate(card.id)}
              className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-all cursor-pointer card-hover"
            >
              <div
                className={`w-12 h-12 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center mb-4`}
              >
                <Icon size={24} className="text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {card.title}
              </h3>
              <p className="text-gray-500 text-sm">{card.description}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Info */}
      <div className="bg-gray-100 rounded-2xl p-6 text-center">
        <p className="text-sm text-gray-600">
          <span className="font-medium">Creator ID:</span>{' '}
          <code className="px-2 py-1 bg-white rounded text-boggler-teal">
            {creatorId}
          </code>
        </p>
      </div>
    </div>
  );
}
